import { useEffect, useRef, useCallback } from 'react';
import { metrics } from '../metrics/client';

/**
 * Custom hook to detect user idle sessions and emit telemetry events.
 *
 * Tracks user activity (mouse, keyboard, click, scroll, touch) and fires a
 * `session.timeout` clickstream event when the user has been idle for longer
 * than the configured threshold.
 *
 * After emitting the event the timer resets so that *recurring* idle periods
 * each produce their own event — this lets us measure how long clinicians
 * leave sessions open without interaction.
 *
 * Activity handlers are throttled (default 5 s) to avoid performance overhead
 * from high-frequency events like `mousemove`.
 *
 * @param {Object}  options
 * @param {boolean} [options.isRecording=false]  Whether a recording is active
 *   (populates `had_active_recording` in the event payload).
 * @param {number}  [options.idleThresholdMs=1800000]  Idle duration before
 *   emitting the event (default 30 minutes).
 * @param {number}  [options.throttleMs=5000]  Minimum interval between
 *   processing activity events (default 5 s).
 */
export const useIdleTimeout = ({
  isRecording = false,
  idleThresholdMs = 30 * 60 * 1000, // 30 minutes
  throttleMs = 5000,
} = {}) => {
  const timerRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const isRecordingRef = useRef(isRecording);

  // Keep recording ref in sync with prop so the timeout callback always
  // reads the latest value without needing to re-register listeners.
  useEffect(() => {
    isRecordingRef.current = isRecording;
  }, [isRecording]);

  // -----------------------------------------------------------------------
  // Idle timer management
  // -----------------------------------------------------------------------

  const startTimer = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);

    timerRef.current = setTimeout(() => {
      const idleDuration = Math.round((Date.now() - lastActivityRef.current) / 1000);

      console.log('[useIdleTimeout] Session idle timeout reached', {
        idle_duration: idleDuration,
        had_active_recording: isRecordingRef.current,
      });

      metrics.add(
        metrics.sessionTimeoutEvent({
          idle_duration: idleDuration,
          had_active_recording: isRecordingRef.current,
        })
      );

      // Reset so the next idle period also emits an event
      lastActivityRef.current = Date.now();
      startTimer();
    }, idleThresholdMs);
  }, [idleThresholdMs]);

  const resetTimer = useCallback(() => {
    lastActivityRef.current = Date.now();
    startTimer();
  }, [startTimer]);

  // -----------------------------------------------------------------------
  // Activity listeners (throttled)
  // -----------------------------------------------------------------------

  useEffect(() => {
    let lastProcessed = 0;

    const onActivity = () => {
      const now = Date.now();
      if (now - lastProcessed < throttleMs) return;
      lastProcessed = now;
      resetTimer();
    };

    const EVENTS = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];

    EVENTS.forEach((evt) =>
      window.addEventListener(evt, onActivity, { passive: true, capture: true })
    );

    // Kick off the initial timer
    resetTimer();

    return () => {
      EVENTS.forEach((evt) =>
        window.removeEventListener(evt, onActivity, { capture: true })
      );
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [resetTimer, throttleMs]);
};
